import {
  ServiceTime,
  Coverage,
  Periodicity,
  EventType,
  MerchantType,
  Affluence,
  TeamSize,
} from './onboarding.model';

import type { ActivityType } from './cluster.model';

/**
 * =========================
 * LABELS UI – ONBOARDING
 * =========================
 * ⚠️ Aucun calcul métier ici
 * Seulement du mapping enum → affichage
 */

/* Types de marchands */
export const MERCHANT_TYPE_LABELS: Record<
  MerchantType,
  {
    label: string;
    emoji: string;
    description: string;
    details: string[];
  }
> = {
  [MerchantType.FIXED]: {
    label: 'Commerce Fixe',
    emoji: '🏪',
    description: 'Point de vente établi et régulier',
    details: [
      'Lieu fixe',
      'Horaires stables',
      'Clientèle locale',
      'Services quotidiens / saisonniers',
    ],
  },
  [MerchantType.ITINERANT]: {
    label: 'Activité Itinérante',
    emoji: '🚚',
    description: 'Activité mobile et événementielle',
    details: [
      'Lieux variables',
      'Événements & marchés',
      'Déplacements réguliers',
      'Services ponctuels',
    ],
  },
};

/* Affluence */
export const AFFLUENCE_LABELS: Record<
  Affluence,
  { label: string; description: string; icon: string }
> = {
  low: { label: 'Faible', description: 'Peu de clients réguliers', icon: '👤' },
  medium: { label: 'Moyen', description: 'Clientèle stable', icon: '👥' },
  high: { label: 'Élevé', description: 'Beaucoup de clients', icon: '👨‍👩‍👧‍👦' },
};

/* Taille d’équipe */
export const TEAM_SIZE_LABELS: Record<
  TeamSize,
  { label: string; description: string }
> = {
  solo: { label: 'Solo', description: 'Vous seul(e)' },
  '2persons': { label: 'Petite équipe', description: '2 personnes' },
  '3plus': { label: 'Grande équipe', description: '3+ personnes' },
  multi_stand: {
    label: 'Plusieurs points de ventes',
    description: 'Plusieurs stands ou points de ventes',
  },
};

/* Services */
export const SERVICE_LABELS: Record<ServiceTime, string> = {
  'Petit-déjeuner': '🌅 Petit-déjeuner',
  Déjeuner: '☀️ Déjeuner',
  Dîner: '🌙 Dîner',
};

/* Couverture */
export const COVERAGE_OPTIONS: {
  id: Coverage;
  label: string;
  description: string;
}[] = [
  {
    id: Coverage.COVERED,
    label: '🏠 Couvert',
    description: "Local fermé, peu d'impact météo",
  },
  {
    id: Coverage.SEMI_COVERED,
    label: '⛱️ Semi-couvert',
    description: 'Terrasse, auvent ou protection partielle',
  },
  {
    id: Coverage.UNCOVERED,
    label: '🌦️ Non couvert',
    description: 'Totalement exposé à la météo',
  },
];

/* Périodicité */
export const PERIODICITY_LABELS: Record<Periodicity, string> = {
  daily: 'Quotidienne',
  weekly: 'Hebdomadaire',
  seasonal: 'Saisonnière',
};

/* Labels d'activité (pour affichage dans les templates) */
export const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  bakery: '🥖 Boulangerie-Pâtisserie',
  sandwich_shop: '🥪 Sandwicherie & Snacking',
  food_truck_burger: '🍔 Food Truck Burger',
  food_truck_world: '🌮 Food Truck Cuisine du Monde',
  food_truck_resto: '🚚 Food Truck Restauration',
  bar_cafe: '☕ Bar & Café',
  ice_cream: '🍦 Glacier / Frozen',
  fresh_produce: '🥬 Primeur / Produits Frais',
  market_deli: '🧀 Traiteur Marché',
};

/* Types d'événements */

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  market: 'Marché',
  festival: 'Festival',
  office: 'Zone de bureaux',
  occasional: 'Occasionnel',
  street: 'Rue / Animation',
  private: 'Événement privé',
  other: 'Autre',
};

// ─────────────────────────────────────────────
// ActivityType config (9 types)
// ─────────────────────────────────────────────

export interface ActivityTypeConfig {
  label: string;
  icon: string;
  description: string;
  coreProducts: string[];
  complementaryProducts: string[];
  occasionalProducts: string[];
}

/**
 * Les 9 activityType avec icône, description et exemples produits.
 * Filtrage par merchantType via ACTIVITY_TYPES_BY_MERCHANT.
 */
export const ACTIVITY_TYPE_CONFIG: Record<ActivityType, ActivityTypeConfig> = {
  bakery: {
    label: 'Boulangerie-Pâtisserie',
    icon: '🥖',
    description: 'Pain, viennoiseries, pâtisseries',
    coreProducts: [
      'Pain (baguettes, pains spéciaux)',
      'Viennoiseries (croissants, pains au chocolat)',
    ],
    complementaryProducts: [
      'Sandwichs préparés',
      'Pâtisseries individuelles',
      'Boissons',
    ],
    occasionalProducts: ['Confiseries', 'Produits de saison'],
  },
  sandwich_shop: {
    label: 'Sandwicherie / Traiteur léger',
    icon: '🥪',
    description: 'Sandwichs, salades, quiches',
    coreProducts: [
      'Sandwichs variés (paninis, wraps, bagels)',
      'Salades composées',
    ],
    complementaryProducts: [
      'Boissons froides',
      'Boissons chaudes',
      'Snacks sucrés',
    ],
    occasionalProducts: ['Soupes (hiver)', 'Plats chauds'],
  },
  food_truck_burger: {
    label: 'Food Truck Burger / Américain',
    icon: '🍔',
    description: 'Burgers, frites, hot-dogs',
    coreProducts: ['Burgers / Sandwichs chauds', 'Frites / Accompagnements'],
    complementaryProducts: ['Boissons', 'Sauces', 'Desserts'],
    occasionalProducts: ['Salades', 'Wraps'],
  },
  food_truck_world: {
    label: 'Food Truck Cuisine du Monde',
    icon: '🌮',
    description: 'Tacos, nems, bobun, kebab',
    coreProducts: ['Tacos / Burritos', 'Nems / Bobun', 'Kebab / Gyros'],
    complementaryProducts: ['Riz / Accompagnements', 'Boissons', 'Sauces'],
    occasionalProducts: ['Desserts exotiques', 'Smoothies tropicaux'],
  },
  food_truck_resto: {
    label: 'Food Truck Restauration',
    icon: '🚚',
    description: 'Plats du jour, grillades, panini',
    coreProducts: ['Plats du jour', 'Grillades', 'Paninis chauds'],
    complementaryProducts: ['Boissons', 'Accompagnements', 'Desserts'],
    occasionalProducts: ['Salades', 'Menus enfants'],
  },
  bar_cafe: {
    label: 'Bar / Café / Salon de thé',
    icon: '☕',
    description: 'Boissons chaudes, froides, snacks',
    coreProducts: [
      'Café (expresso, cappuccino)',
      'Thé / Infusions',
      'Chocolat chaud',
    ],
    complementaryProducts: [
      'Boissons froides',
      'Snacks sucrés',
      'Snacks salés',
    ],
    occasionalProducts: ['Sandwichs / Paninis', 'Salades', 'Plats du jour'],
  },
  ice_cream: {
    label: 'Glacier / Frozen',
    icon: '🍦',
    description: 'Glaces, sorbets, milk-shakes, crêpes',
    coreProducts: [
      'Glaces (cornets, pots, coupes)',
      'Crêpes sucrées / Gaufres',
    ],
    complementaryProducts: ['Toppings', 'Sorbets', 'Smoothies / Milkshakes'],
    occasionalProducts: ['Crêpes salées', 'Pâtisseries'],
  },
  fresh_produce: {
    label: 'Primeur / Produits Frais',
    icon: '🥬',
    description: 'Fruits, légumes, jus pressés',
    coreProducts: [
      'Fruits & Légumes de saison',
      'Fromages artisanaux',
      'Produits bio / locaux',
    ],
    complementaryProducts: [
      'Œufs frais',
      'Produits laitiers',
      'Confitures / Miels',
    ],
    occasionalProducts: ['Viandes / Charcuteries', 'Pains artisanaux'],
  },
  market_deli: {
    label: 'Traiteur Marché',
    icon: '🧀',
    description: 'Charcuterie, fromage, plats préparés',
    coreProducts: [
      'Plats cuisinés (viandes, poissons)',
      'Entrées (terrines, salades)',
      'Desserts',
    ],
    complementaryProducts: ['Plateaux-repas', 'Fromages', 'Vins / Boissons'],
    occasionalProducts: ['Sandwichs / Wraps', 'Conserves artisanales'],
  },
};

/**
 * Mapping merchantType → activityTypes autorisés.
 * Fixe : 7/9 (pas food_truck_world, food_truck_resto)
 * Itinérant : 7/9 (pas sandwich_shop, bar_cafe)
 */
export const ACTIVITY_TYPES_BY_MERCHANT: Record<MerchantType, ActivityType[]> =
  {
    [MerchantType.FIXED]: [
      'bakery',
      'sandwich_shop',
      'food_truck_burger',
      'bar_cafe',
      'ice_cream',
      'fresh_produce',
      'market_deli',
    ],
    [MerchantType.ITINERANT]: [
      'bakery',
      'food_truck_burger',
      'food_truck_world',
      'food_truck_resto',
      'ice_cream',
      'fresh_produce',
      'market_deli',
    ],
  };

/**
 * Labels pour le positionnement (premium/standard/economy)
 */
export const POSITIONING_LABELS: Record<
  string,
  { label: string; icon: string; description: string }
> = {
  premium: {
    label: 'Premium',
    icon: '💎',
    description: 'Produits haut de gamme, marges élevées',
  },
  standard: {
    label: 'Standard',
    icon: '⭐',
    description: 'Rapport qualité-prix équilibré',
  },
  economy: {
    label: 'Économique',
    icon: '💰',
    description: 'Produits accessibles, volume important',
  },
};
